import { useParams } from 'react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Quote } from 'receivable-invoices-js/react/api/models/quote';
import { useFetchQuote } from 'receivable-invoices-js/react/api/queries/fetch-quote/fetch-quote';
import { useFetchReceivableInvoicesUpload } from 'receivable-invoices-js/react/api/queries/fetch-receivable-invoices-upload/fetch-receivable-invoices-upload';
import { useFetchClientPortalSettings } from 'receivable-invoices-js/react/api/queries/fetch-client-portal-settings';
import type { Attachment } from 'receivable-invoices-js/react/components/shared/responsive-view/types';
import { useLocaleManager } from 'receivable-invoices-js/react/hooks/use-locale-manager';
import { fetchPdfSrc } from 'receivable-invoices-js/react/utils/pdf-utils/pdf-utils';

interface UseRouteQueryReturn {
  isLoading: boolean;
  isError: boolean;
  quote?: Quote;
  attachments: Attachment[];
  isClientPortalEnabled?: boolean;
}

export function useRouteQuery(): UseRouteQueryReturn {
  const { id } = useParams<{ id: string }>();
  const localeManager = useLocaleManager();

  const quoteId = id || '';
  const {
    data: quote,
    isLoading: quoteLoading,
    isError: quoteError,
  } = useFetchQuote({ id: quoteId });

  // Fetch PDF source separately
  const { data: pdfSrc, isLoading: pdfLoading } = useQuery({
    queryKey: ['quote-pdf-src', quoteId, quote?.pdfUrl],
    queryFn: () => fetchPdfSrc(quote?.pdfUrl || ''),
    enabled: Boolean(quote?.pdfUrl),
  });

  // Set locale when quote is loaded
  useEffect(() => {
    if (quote?.locale) {
      localeManager.setLocale(quote.locale);
    }
  }, [quote?.locale, localeManager]);

  // Client portal settings query
  const clientId = quote?.customerSnapshot.id || '';
  const {
    data: clientPortalSettings,
    isLoading: clientPortalSettingsLoading,
    isError: clientPortalSettingsError,
  } = useFetchClientPortalSettings({ clientId }, {}, { enabled: Boolean(clientId) });

  // Get attachment IDs from relationships
  const receivableInvoicingUploadId = quote?.receivableInvoicingUploadId || '';
  const termsConditionsUploadId = quote?.termsConditionsUploadId || '';

  // Fetch attachments
  const {
    data: receivableInvoicingUploadQuery,
    isLoading: receivableInvoicingUploadLoading,
    isError: receivableInvoicingUploadError,
  } = useFetchReceivableInvoicesUpload(
    receivableInvoicingUploadId,
    {},
    { enabled: Boolean(receivableInvoicingUploadId) }
  );

  const {
    data: termsConditionsUploadQuery,
    isLoading: termsConditionsUploadLoading,
    isError: termsConditionsUploadError,
  } = useFetchReceivableInvoicesUpload(
    termsConditionsUploadId,
    {},
    { enabled: Boolean(termsConditionsUploadId) }
  );

  // Process attachments
  const getAttachments = useCallback(() => {
    const attachments = [];

    if (receivableInvoicingUploadQuery) {
      attachments.push({
        id: 'attachment',
        url: receivableInvoicingUploadQuery.url,
        filename: receivableInvoicingUploadQuery.filename,
        src:
          typeof receivableInvoicingUploadQuery.src === 'string'
            ? receivableInvoicingUploadQuery.src
            : undefined,
      });
    }

    if (termsConditionsUploadQuery) {
      attachments.push({
        id: 'terms-conditions',
        url: termsConditionsUploadQuery.url,
        filename: termsConditionsUploadQuery.filename,
        src:
          typeof termsConditionsUploadQuery.src === 'string'
            ? termsConditionsUploadQuery.src
            : undefined,
      });
    }

    return attachments;
  }, [receivableInvoicingUploadQuery, termsConditionsUploadQuery]);

  const isError = useMemo(() => {
    if (
      quoteError ||
      clientPortalSettingsError ||
      receivableInvoicingUploadError ||
      termsConditionsUploadError
    ) {
      return true;
    }

    return false;
  }, [
    quoteError,
    clientPortalSettingsError,
    receivableInvoicingUploadError,
    termsConditionsUploadError,
  ]);

  const isLoading = useMemo(() => {
    return (
      quoteLoading ||
      clientPortalSettingsLoading ||
      receivableInvoicingUploadLoading ||
      termsConditionsUploadLoading ||
      pdfLoading
    );
  }, [
    quoteLoading,
    clientPortalSettingsLoading,
    receivableInvoicingUploadLoading,
    termsConditionsUploadLoading,
    pdfLoading,
  ]);

  return {
    quote: quote ? { ...quote, pdfSrc } : undefined,
    attachments: getAttachments(),
    isClientPortalEnabled: clientPortalSettings?.clientPortalEnabled ?? false,
    isLoading,
    isError,
  };
}
